import React, { useEffect, useState } from "react";
import Select from "react-select";
import Sidebar from "../../components/Layout/Sidebar";
import api from "../../services/api"; // adjust path
import invoiceService from "../../services/invoiceService";

export default function CreateInvoice() {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [services, setServices] = useState([]);
  const [rows, setRows] = useState([]);
  const [paidAmount, setPaidAmount] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split("T")[0]);

  const [loading, setLoading] = useState(false);   // 🔹 spinner state
  const [message, setMessage] = useState(null);   

  const addRow = () => {
    setRows([...rows, { serviceId: "", description: "", rate: "", qty: "", total: 0 }]);
  };

  // 🟢 Remove Row
  const removeRow = (index) => {
    const updated = [...rows];
    updated.splice(index, 1);
    setRows(updated);
  };

  // 🟢 Handle Input Change
  const handleInputChange = (index, field, value) => {
    const updated = [...rows];
    updated[index][field] = value;

    if (field === "rate" || field === "qty") {
      const rate = parseFloat(updated[index].rate) || 0;
      const qty = parseFloat(updated[index].qty) || 0;
      updated[index].total = (rate * qty).toFixed(2);
    }

    setRows(updated);
  };  

  const fetchInvoiceNumber = async () => {
    try {
      const res = await invoiceService.getNextInvoiceNumber()
      if(res.status) {
        setInvoiceNumber(res.invoice_number);
      }
    }

    catch (err){
      console.error("Error fetching invoice number",err);
    }
  }
  // fetch clients on load
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const token = localStorage.getItem("auth_token");
        const res = await api.get("/api/clients", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data.status) {
          setClients(res.data.clients);
        }
      } catch (err) {
        console.error("Error fetching clients", err);
      }
    };

    const fetchServices = async () => {
      try {
        const token = localStorage.getItem("auth_token");
        const res = await api.get("/api/list-services", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data.status) {
          setServices(res.data.service);
        }
      } catch (err) {
        console.error("Error fetching services", err);
      }
    };

    fetchClients();
    fetchInvoiceNumber();
    fetchServices();
  }, []);

  // react-select options
  const clientOptions = clients.map((c) => ({
    value: c.id,
    label: c.email,
    client: c,
  }));
  const serviceOptions = services.map((s) => ({
    value: s.id,
    label: s.name,
    service: s,
  }));


  const handleClientChange = (option) => {
    setSelectedClient(option.client);
  };
  const handleServiceChange = (selected, index) => {
    const updated = [...rows];
    updated[index] = {
      ...updated[index],
      serviceId: selected.service.id,
      description: selected.service.name,
      rate: "",
      qty: "",
      total: 0,
    };
    setRows(updated);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true); // 🔹 show spinner

      const token = localStorage.getItem("auth_token");
      const payload = {
        client_id: selectedClient?.id,
        invoice_number: invoiceNumber,
        date: invoiceDate,
        services: rows.map((row) => ({
          service_id: row.serviceId,
          description: row.description,
          qty: parseFloat(row.qty) || 0,
          rate: parseFloat(row.rate) || 0,
          total: parseFloat(row.total) || 0,
        })),
        grand_total: parseFloat(grandTotal),
        paid_amount: parseFloat(paidAmount) || 0,
        balance_due: parseFloat(grandTotal) - (parseFloat(paidAmount) || 0),
      };

      const res = await api.post("/api/save-invoices", payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data.status) {
        setMessage({ type: "success", text: "Invoice saved successfully!" });

        // 🔹 Reset form instead of full reload
        setSelectedClient(null);
        setRows([]);
        setPaidAmount("");
        fetchInvoiceNumber(); // get new invoice number again
      } else {
        setMessage({ type: "error", text: res.data.message || "Error saving invoice" });
      }
    } catch (err) {
      console.error("Error saving invoice", err);
      setMessage({ type: "error", text: "Something went wrong.Did you correctly fill the form data?" });
    } finally {
      setLoading(false); // 🔹 hide spinner
    }
  };

//save & print
const handleSaveAndPrint = async () => {
  try {
    setLoading(true);
    const token = localStorage.getItem("auth_token");

    const payload = {
      client_id: selectedClient?.id,
      invoice_number: invoiceNumber,
      date: invoiceDate,
      services: rows.map((row) => ({
        service_id: row.serviceId,
        description: row.description,
        qty: parseFloat(row.qty) || 0,
        rate: parseFloat(row.rate) || 0,
        total: parseFloat(row.total) || 0,
      })),
      grand_total: parseFloat(grandTotal),
      paid_amount: parseFloat(paidAmount) || 0,
      balance_due: parseFloat(grandTotal) - (parseFloat(paidAmount) || 0),
    };

    const response = await api.post("/api/save-invoices-print", payload, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: "blob",
    });

    const pdfBlob = new Blob([response.data], { type: "application/pdf" });
    const pdfUrl = URL.createObjectURL(pdfBlob);

    // Just open in new tab — NO print()
    const printWindow = window.open(pdfUrl, "_blank");

    if (printWindow) {
      printWindow.focus();
    }

    // Optional: Clean up after 30 seconds (user may close tab)
    setTimeout(() => {
      URL.revokeObjectURL(pdfUrl);
    }, 30000);

    setMessage({ type: "success", text: "Invoice saved & opened for review!" });
    resetForm();
  } catch (err) {
    console.error(err);
    setMessage({
      type: "error",
      text: err.response?.data?.message || "Failed to generate PDF",
    });
  } finally {
    setLoading(false);
  }
};

const resetForm = () => {
  setSelectedClient(null);
  setRows([]);
  setPaidAmount("");
  fetchInvoiceNumber();
};

  const grandTotal = rows.reduce((sum, row) => sum + parseFloat(row.total || 0), 0).toFixed(2);

  return (
    <div className="min-h-screen bg-gray-100 flex">
      <Sidebar />

      <div className="flex-1 md:ml-64 mt-14 md:mt-0 px-4 py-4 md:px-6 md:py-6">
        {/* Spinner overlay */}
        {loading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-white"></div>
          </div>
        )}

        {/* Popup message */}
        {message && (
          <div
            className={`fixed top-5 right-5 px-4 py-2 rounded shadow-lg z-50 text-white ${
              message.type === "success" ? "bg-green-600" : "bg-red-600"
            }`}
          >
            {message.text}
            <button
              onClick={() => setMessage(null)}
              className="ml-3 text-sm underline"
            >
              Close
            </button>
          </div>
        )}

        <div className="max-w-6xl mx-auto mb-6">
          <h1 className="text-3xl font-bold text-white bg-black p-4 rounded shadow text-center">
            Create Invoice
          </h1>
        </div>

        {/* Client Section */}
        <div className="bg-white shadow rounded p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Client Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm mb-1">Client Email</label>
              <Select
                options={clientOptions}
                onChange={handleClientChange}
                placeholder="Search by email..."
              />
            </div>

            <div>
              <label className="block text-sm mb-1">Company Name</label>
              <input
                type="text"
                className="w-full border rounded px-3 py-2"
                value={selectedClient?.company || ""}
                readOnly
              />
            </div>

            <div>
              <label className="block text-sm mb-1">Client Name</label>
              <input
                type="text"
                className="w-full border rounded px-3 py-2"
                value={selectedClient?.name || ""}
                readOnly
              />
            </div>

            <div>
              <label className="block text-sm mb-1">Phone</label>
              <input
                type="text"
                className="w-full border rounded px-3 py-2"
                value={selectedClient?.phone || ""}
                readOnly
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm mb-1">Address</label>
              <textarea
                className="w-full border rounded px-3 py-2"
                rows="2"
                value={selectedClient?.address || ""}
                readOnly
              />
            </div>
          </div>
        </div>

        {/* Invoice Details */}
        <div className="bg-white shadow rounded p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Invoice Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm mb-1">Invoice No</label>
              <input
                type="text"
                className="w-full border rounded px-3 py-2"
                value={invoiceNumber}
                readOnly
              />
            </div>
            <div>
              <label className="block text-sm mb-1">Date</label>
              <input
                type="date"
                className="w-full border rounded px-3 py-2"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Services Section */}
        <div className="bg-white shadow rounded p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Services</h2>
          <table className="w-full border text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="border px-2 py-1">#</th>
                <th className="border px-2 py-1">Code</th>
                <th className="border px-2 py-1">Description</th>
                <th className="border px-2 py-1">Qty</th>
                <th className="border px-2 py-1">Rate</th>
                <th className="border px-2 py-1">Total</th>
                <th className="border px-2 py-1">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={index}>
                  <td className="border px-2 py-1 text-center">{index + 1}</td>
                  <td className="border px-2 py-1 text-center">{row.serviceId || "--"}</td>
                  <td className="border px-2 py-1">
                    <Select
                      options={serviceOptions}
                      onChange={(selected) => handleServiceChange(selected, index)}
                      placeholder="Select service..."
                    />
                  </td>
                  <td className="border px-2 py-1">
                    <input
                      type="number"
                      step="1"
                      className="w-full border px-2 py-1"
                      value={row.qty}
                      onChange={(e) => handleInputChange(index, "qty", e.target.value)}
                    />
                  </td>
                  <td className="border px-2 py-1">
                    <input
                      type="number"
                      step="0.01"
                      className="w-full border px-2 py-1"
                      value={row.rate}
                      onChange={(e) => handleInputChange(index, "rate", e.target.value)}
                    />
                  </td>
                  <td className="border px-2 py-1 text-right">{row.total}</td>
                  <td className="border px-2 py-1 text-center">
                    <button
                      onClick={() => removeRow(index)}
                      className="text-red-500 hover:underline"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-3">
            <button
              onClick={addRow}
              className="bg-blue-500 text-white px-3 py-1 rounded shadow hover:bg-blue-600"
            >
              + Add Service
            </button>
          </div>

          {/* Totals */}
        <div className="mt-6 space-y-3">
          {/* Grand Total */}
          <div className="flex justify-end items-center gap-4">
            <span className="font-semibold">Grand Total:</span>
            <span className="text-lg font-bold">
              LKR {parseFloat(grandTotal).toFixed(2)}
            </span>
          </div>

          {/* Paid Amount */}
        <div className="flex justify-end items-center gap-4">
          <label className="font-semibold">Paid Amount:</label>
          <input
            type="number"
            step="0.01"
            className="w-32 border px-3 py-2 text-right"
            value={paidAmount}
            onChange={(e) => setPaidAmount(e.target.value)}
            onBlur={(e) =>
              setPaidAmount(parseFloat(e.target.value || 0).toFixed(2))
            }
          />
        </div>

          {/* Balance Due */}
          <div className="flex justify-end items-center gap-4">
            <span className="font-semibold">Balance Due:</span>
            <span className="text-lg font-bold">
              LKR {(
                parseFloat(grandTotal) - (parseFloat(paidAmount) || 0)
              ).toFixed(2)}
            </span>
          </div>
        </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-4">
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="bg-gray-500 text-white px-6 py-2 rounded shadow hover:bg-gray-600 disabled:opacity-50"
          >
            {loading ? "Saving..." : "Save"}
          </button>
          <button
            onClick={handleSaveAndPrint}
            disabled={loading}
            className="bg-black text-white px-6 py-2 rounded shadow hover:bg-gray-800 disabled:opacity-50"
          >
            {loading ? "Processing…" : "Save & Print"}
          </button>
        </div>
      </div>
    </div>
  );
}
